import React, { useState, useCallback, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Typography, Box, Skeleton } from "@mui/material";
import styles from "./CustomChart.module.css";
import OrderOverviewTable from "../TableBackup";

const DEFAULT_COLORS = [
  "#354ACD",
  "#5B6FE6",
  "#8F9AE3",
  "#B6BFF0",
  "#2E90FA",
  "#12B76A",
  "#F79009",
  "#F97066",
  "#667F93",
  "#A3D9A5",
  "#FFD66B",
  "#FF7043",
];

const RADIAN = Math.PI / 180;
const MAX_VISIBLE = 5;
const OTHERS_COLOR = "#B3C5D4";

/**
 * Keep the first N items in their original order, merge the rest into "Others".
 * No sorting — preserves the data order from the API.
 */
const limitToFirstN = (items, n = MAX_VISIBLE) => {
  if (!items?.length) return [];
  if (items.length <= n) return items;

  const visible = items.slice(0, n);
  const rest = items.slice(n);
  const othersValue = rest.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  if (othersValue > 0) {
    visible.push({
      name: "Others",
      value: othersValue,
      unit: visible[0]?.unit || "",
      _isOthers: true,
      _count: rest.length,
    });
  }

  return visible;
};

const getContrastColor = (hex) => {
  if (!hex || typeof hex !== "string") return "#FFFFFF";
  const h = hex.replace("#", "");
  if (h.length !== 6) return "#FFFFFF";
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(isNaN)) return "#FFFFFF";
  return (r * 299 + g * 587 + b * 114) / 1000 > 155 ? "#1A2025" : "#FFFFFF";
};

// ── Skeleton ──
const CustomLoadingSkeleton = ({ count = 5 }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      gap: 4,
      py: 3,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flex: "0 0 220px",
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width="100%"
          height={40}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Box>
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        flex: 1,
      }}
    >
      <Box sx={{ position: "relative", width: 280, height: 150 }}>
        <Skeleton
          variant="rounded"
          width={280}
          height={150}
          sx={{ borderTopLeftRadius: 280, borderTopRightRadius: 280 }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 180,
            height: 100,
            bgcolor: "background.default",
            borderTopLeftRadius: 180,
            borderTopRightRadius: 180,
          }}
        />
      </Box>
    </Box>
  </Box>
);

// ── Tooltip helpers ──
const formatKey = (key) =>
  String(key || "")
    .replace(/,/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const SKIP_KEYS = new Set([
  "name",
  "value",
  "unit",
  "fill",
  "stroke",
  "cx",
  "cy",
  "innerRadius",
  "outerRadius",
  "startAngle",
  "endAngle",
  "paddingAngle",
  "midAngle",
  "percent",
  "tooltipPayload",
  "tooltipPosition",
  "payload",
  "_isOthers",
  "_count",
]);

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  const { name, value, _isOthers, _count } = entry;
  const color = entry.fill || payload[0].color;
  const percent = payload[0].percent;

  // All additional data fields
  const extraFields = Object.entries(entry).filter(
    ([key]) => !SKIP_KEYS.has(key),
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E8ECF0",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        maxWidth: 280,
      }}
    >
      {/* Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1A2025" }}>
          {_isOthers ? `Others (${_count} items)` : name}
        </Typography>
      </Box>

      {/* Value */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#4D5F6E" }}>Value</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1A2025" }}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
      </Box>

      {/* Percentage */}
      {percent != null && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#4D5F6E" }}>Share</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#354ACD" }}>
            {(percent * 100).toFixed(1)}%
          </Typography>
        </Box>
      )}

      {/* All additional data fields */}
      {extraFields.map(([key, val]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#667F93" }}>
            {formatKey(key)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#1A2025" }}>
            {typeof val === "number"
              ? String(val)
              : String(val ?? "-")
                  .replace(/,/g, "")
                  .replace(/_/g, " ")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Legend Item ──
const LegendItem = ({ name, value, unit, color, isOthers, othersCount }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      py: 1.25,
      px: 1.5,
      borderRadius: "10px",
      border: "1px solid #E4E7EC",
      backgroundColor: "#FFFFFF",
    }}
  >
    <Box display="flex" alignItems="center" gap={1} minWidth={0}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
          boxShadow: `0 0 0 3px ${color}22`,
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: 12, md: 13 },
          fontWeight: 500,
          color: "#344054",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {isOthers ? `Others (${othersCount})` : name}
      </Typography>
    </Box>
    <Box display="flex" alignItems="baseline" gap="3px" flexShrink={0}>
      <Typography
        sx={{ fontSize: { xs: 13, md: 15 }, fontWeight: 700, color: "#1A2025" }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: 11, color: "#667F93" }}>{unit}</Typography>
      )}
    </Box>
  </Box>
);

// ── Chart Content ──
const CustomChartContent = ({
  chartData,
  chartColors,
  innerRadius,
  outerRadius,
  renderLabel,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 2, md: 5 },
      px: { xs: 1.5, md: 2 },
      py: 2,
      width: "100%",
    }}
  >
    {/* Legend */}
    <Box
      sx={{
        flex: "0 0 auto",
        width: { xs: "100%", md: 240 },
        maxHeight: { xs: 220, md: 300 },
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        scrollbarWidth: "thin",
        scrollbarColor: "#E4E7EC transparent",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D9E1E7",
          borderRadius: 4,
        },
      }}
    >
      {chartData.map((item, index) => (
        <LegendItem
          key={`${item.name}-${index}`}
          name={item.name}
          value={item.value}
          unit={item.unit}
          color={chartColors[index % chartColors.length]}
          isOthers={!!item._isOthers}
          othersCount={item._count}
        />
      ))}
    </Box>

    {/* Chart */}
    <Box
      sx={{
        flex: { xs: "0 0 auto", md: 1 },
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        width: "100%",
        maxWidth: 460,
        minHeight: outerRadius + 40,
      }}
    >
      <ResponsiveContainer width="100%" height={outerRadius + 40}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="95%"
            startAngle={180}
            endAngle={0}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            dataKey="value"
            labelLine={false}
            label={renderLabel}
            paddingAngle={1}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`${entry.name}-${index}`}
                fill={chartColors[index % chartColors.length]}
                stroke="#ffffff"
                // strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  </Box>
);

// ── Main Component ──
const SemiCircleChart = ({
  data = [],
  colors = [],
  innerRadius = 80,
  outerRadius = 140,
  headerConfig,
  rows,
  color = [],
  refreshTrigger,
}) => {
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(data);
  const [activeView] = useState("graph");
  const [openExpand] = useState(false);

  useEffect(() => {
    setRawData(data);
  }, [data]);

  // Top 5 + Others
  // First 5 items in original order + "Others" for the rest
  const chartData = useMemo(
    () => limitToFirstN(rawData, MAX_VISIBLE),
    [rawData],
  );

  // Build color array — use OTHERS_COLOR for the "Others" slice
  const chartColors = useMemo(() => {
    const base = colors.length > 0 ? colors : DEFAULT_COLORS;
    return chartData.map((item, i) =>
      item.name === "Others" ? OTHERS_COLOR : base[i % base.length],
    );
  }, [colors, chartData]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setRawData([...data]);
      setLoading(false);
    }, 1500);
  }, [data]);

  useEffect(() => {
    if (refreshTrigger) handleRefresh();
  }, [refreshTrigger, handleRefresh]);

  useEffect(() => {
    setRawData(data);
  }, [data]);

  // Render labels OUTSIDE the arc for small slices, INSIDE for large ones
  const renderCustomizedLabel = useCallback(
    ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
      if (percent < 0.03) return null;

      const color = chartColors[index % chartColors.length];
      const isSmall = percent < 0.08;
      const displayText = `${(percent * 100).toFixed(1)}%`;

      if (isSmall) {
        // External label with a line
        const outerR = outerRadius + 16;
        const x = cx + outerR * Math.cos(-midAngle * RADIAN);
        const y = cy + outerR * Math.sin(-midAngle * RADIAN);
        return (
          <text
            x={x}
            y={y}
            fill={color}
            textAnchor={x > cx ? "start" : "end"}
            dominantBaseline="central"
            fontSize={10}
            fontWeight={600}
          >
            {displayText}
          </text>
        );
      }

      // Internal label
      const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text
          x={x}
          y={y}
          fill={getContrastColor(color)}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          fontWeight={700}
        >
          {displayText}
        </text>
      );
    },
    [chartColors],
  );

  const renderBody = () => {
    if (loading && activeView === "graph") {
      return (
        <CustomLoadingSkeleton count={Math.min(chartData.length || 5, 6)} />
      );
    }
    if (activeView === "graph") {
      return (
        <CustomChartContent
          chartData={chartData}
          chartColors={chartColors}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          renderLabel={renderCustomizedLabel}
        />
      );
    }
    return (
      <OrderOverviewTable
        headerConfig={headerConfig}
        rows={rows}
        colors={color}
        loading={loading}
        maxHeight={openExpand ? "70vh" : "320px"}
      />
    );
  };

  return (
    <Box className={activeView === "graph" ? styles.graphBG : ""}>
      {renderBody()}
    </Box>
  );
};

export default SemiCircleChart;
